import React, { useState } from "react";
import {
  Container,
  Grid,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Fab,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { commonTextFieldProps } from "../../utils/styles";
import OwnedRentalBookings from "./components/OwnedRentalBookings";
import NavigationHeader from "../../components/NavigationHeader";

const RentalBooking: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    vehicleNumber: "",
    startDate: "",
    routeFrom: "",
    routeTo: "",
  });

  // Add trigger state for applying filters
  const [applyFilterTrigger, setApplyFilterTrigger] = useState(0);

  const handleFilterChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setSearchFilters({
      vehicleNumber: "",
      startDate: "",
      routeFrom: "",
      routeTo: "",
    });
    // Trigger API call after clearing filters
    setApplyFilterTrigger((prev) => prev + 1);
  };

  const handleApplyFilters = () => {
    // Trigger API call by incrementing the trigger
    setApplyFilterTrigger((prev) => prev + 1);
  };

  const handleNewBooking = () => {
    navigate("/rental/new");
  };

  const handleEditBooking = (id: string) => {
    navigate(`/rental/${id}`);
  };

  const handleDailyLogs = (
    id: string,
    vehicleId: string,
    vehicleNumber: string,
    type: string
  ) => {
    navigate(
      `/rental/${id}/logs?type=${
        type || "SCHEDULED"
      }&tab=LOGS&vehicleId=${vehicleId}&vehicleNumber=${vehicleNumber}`
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <NavigationHeader
        title="Bookings"
        button={{
          label: "Add Booking",
          onClick: handleNewBooking,
          icon: <AddIcon />,
        }}
        backNavigationEnabled={false}
      />

      {/* Search & Filter Section */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, md: 1.5 },
          px: { xs: 2, md: 2 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          mb: 2,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Vehicle Number"
              placeholder="e.g., MH12AB1234"
              value={searchFilters?.vehicleNumber}
              onChange={(e) =>
                handleFilterChange("vehicleNumber", e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "0.9rem" }} color="action" />
                  </InputAdornment>
                ),
              }}
              {...commonTextFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              type="date"
              label="Start Date (From)"
              value={searchFilters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              {...commonTextFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Route From"
              placeholder="e.g., Mumbai"
              value={searchFilters.routeFrom}
              onChange={(e) => handleFilterChange("routeFrom", e.target.value)}
              InputProps={{
                startAdornment: <></>,
              }}
              {...commonTextFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Route To"
              placeholder="e.g., Pune"
              value={searchFilters.routeTo}
              onChange={(e) => handleFilterChange("routeTo", e.target.value)}
              InputProps={{
                startAdornment: <></>,
              }}
              {...commonTextFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4} sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleApplyFilters}
              sx={{
                fontSize: "0.6rem",
                px: 3,
                alignSelf: { xs: "flex-start", sm: "auto" },
                height: 30,
              }}
            >
              Apply
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={clearFilters}
              sx={{
                fontSize: "0.6rem",
                px: 3,
                alignSelf: { xs: "flex-start", sm: "auto" },
                height: 30,
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Vehicle RentalIncome Groups */}
      <OwnedRentalBookings
        handleEditBooking={handleEditBooking}
        handleDailyLogs={handleDailyLogs}
        handleNewBooking={handleNewBooking}
        searchFilters={searchFilters}
        applyFilterTrigger={applyFilterTrigger}
      />

      {/* Floating Action Button for mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add booking"
          size="large"
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            boxShadow: theme.shadows[6],
          }}
          onClick={handleNewBooking}
        >
          <AddIcon sx={{ fontSize: 28 }} />
        </Fab>
      )}
    </Container>
  );
};

export default RentalBooking;
